/**
 * The modal shell shared by `Dialog` and `AlertDialog`: open-state tracking,
 * the scrim, the panel, and the header/footer/title/description classes.
 * Both primitives render their own Radix parts; this module only owns the
 * design so the two stay identical.
 */
import * as React from "react";

import { cn } from "@src/lib/utils/cn";
import type { TextV4Variant } from "@src/lib/ui/Text";

/**
 * Whether the enclosing overlay is open. `true` outside any dialog, so
 * `useFrozenWhileClosed` is a no-op when a part renders on its own.
 */
export const OverlayOpenContext = React.createContext(true);

/**
 * Returns `value` while the enclosing dialog is open, and the last value seen
 * while it was open once it starts closing. Radix keeps the content mounted
 * until the close animation ends; callers often clear the data it renders
 * (the selected row, an error) in the same update that closes it, which made
 * the panel flash empty or change copy on its way out.
 */
export function useFrozenWhileClosed<T>(value: T): T {
  const open = React.useContext(OverlayOpenContext);
  const frozen = React.useRef(value);
  React.useEffect(() => {
    if (open) frozen.current = value;
  });
  return open ? value : frozen.current;
}

type ControllableOpenOptions = {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

/**
 * Tracks a dialog's open state so the root can publish it on
 * `OverlayOpenContext`. Controlled when `open` is passed (the caller owns the
 * state and `onOpenChange` only reports requests), uncontrolled otherwise,
 * starting from `defaultOpen`: the same contract as Radix's `Root`.
 */
export function useControllableOpen({
  open: openProp,
  defaultOpen = false,
  onOpenChange,
}: ControllableOpenOptions): [boolean, (open: boolean) => void] {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : uncontrolledOpen;
  const onOpenChangeRef = React.useRef(onOpenChange);
  React.useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  });
  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChangeRef.current?.(next);
    },
    [isControlled],
  );
  return [open, setOpen];
}

/**
 * Pulls the Tailwind `z-*` utility out of a resolved className.
 *
 * A dialog's scrim has to sit on the same stacking layer as the panel it dims.
 * Callers routinely raise only the content to clear app chrome (the builder's
 * floating toolbar is `z-[750]`), which used to leave the overlay behind on the
 * default `z-50` — so that chrome kept rendering undimmed on top of the backdrop
 * while the panel floated above it. Forwarding the content's layer to the overlay
 * makes the pair move together, whatever a caller picks. See OPUS-17424.
 *
 * `className` is already run through `cn` (tailwind-merge) by the caller, so at
 * most one `z-*` utility survives here.
 */
export const getZIndexClass = (className: string): string | undefined =>
  className.match(/(?:^|\s)(!?-?z-(?:\[[^\]\s]+\]|\d+)!?)(?=\s|$)/)?.[1];

/**
 * Whether the caller sizes the dialog itself with an unprefixed `max-w-*` or
 * an explicit width (`w-[600px]`, `w-96`, `w-1/2`, `w-screen`, `size-[600px]`,
 * `size-96`, with or without `!`). Only dialogs with no width of their own get
 * the default `sm:max-w-md`: tailwind-merge only dedupes within one variant,
 * so ours would otherwise cap theirs at `sm` and up.
 *
 * `min-w-*` deliberately doesn't count: those callers keep the default
 * `w-full`, so dropping the cap would stretch them to the viewport; with it,
 * min-width beats max-width and they render at their minimum, as on main.
 * `w-full` keeps the cap; `w-auto`/`w-fit`/`w-max`/`w-min` are content-sized
 * and handled by `hasIntrinsicWidth`. The `max-w-[calc(100%-2rem)]` gutter
 * stays unless the caller replaces it, so fixed widths still fit on mobile.
 */
const setsOwnWidth = (className: string | undefined): boolean =>
  /(?:^|\s)!?(?:max-w-\S+|w-(?:\[[^\]\s]+\]|\d+(?:\.\d+)?|\d+\/\d+|screen)|size-(?:\[[^\]\s]+\]|\d+(?:\.\d+)?))!?(?=\s|$)/.test(
    className ?? "",
  );

/**
 * Whether the resolved width is content-sized (`w-auto`, `w-fit`, …). The
 * content is a size container (`@container`), and inline-size containment
 * collapses a content-sized box to zero width, so those dialogs skip it and
 * the header/footer fall back to viewport breakpoints.
 */
const hasIntrinsicWidth = (className: string): boolean =>
  /(?:^|\s)(?:[\w-]+:)*(?:w|size|max-w)-(?:auto|fit|max|min)!?(?=\s|$)/.test(
    className,
  );

/**
 * Whether the caller sets a viewport-breakpoint (`sm:`…`2xl:`) utility from
 * one of `groups`. tailwind-merge doesn't dedupe `sm:` against `@sm:`, and the
 * container rules compile after the media rules, so a caller's
 * `sm:justify-between` would lose to a default `@sm:justify-end`. When the
 * caller overrides at a viewport breakpoint, the part uses viewport defaults
 * instead, which twMerge then dedupes against the caller's classes.
 */
const hasViewportOverride = (
  className: string | undefined,
  groups: RegExp,
): boolean =>
  new RegExp(
    String.raw`(?:^|\s)(?:[\w-]+:)*(?:sm|md|lg|xl|2xl):(?:${groups.source})`,
  ).test(className ?? "");

const FOOTER_LAYOUT_UTILITIES = /flex-(?:row|col)|justify-|items-/;
const HEADER_LAYOUT_UTILITIES =
  /text-(?:left|right|center|start|end|justify)\b/;

export type DialogContentLayout = {
  /** Whether the content is a `@container`, so parts can use `@sm:`. */
  isContainer: boolean;
  /** Whether the content renders a corner close button the header must clear. */
  hasCloseButton: boolean;
  /** Called by a mounted description; returns its unregister. */
  registerDescription: () => () => void;
  /**
   * Called by a mounted title with its size, so the close button can line up
   * with the title's first line; returns its unregister.
   */
  reportTitleSize: (size: DialogTitleSize) => () => void;
};

const noRegistration = (): (() => void) => () => undefined;

export const DialogContentLayoutContext =
  React.createContext<DialogContentLayout>({
    isContainer: false,
    hasCloseButton: false,
    registerDescription: noRegistration,
    reportTitleSize: noRegistration,
  });

/**
 * Modal open/close motion shared by the scrim and the panel: tailwindcss-animate
 * enter/exit keyframes on Radix's data-state, timed by the --modal-* tokens in
 * global.css. The durations are data-state variants so they outrank the
 * plugin's default animate-in/animate-out duration. Reduced motion drops the
 * animation; Radix Presence then unmounts on close straight away.
 */
export const MODAL_MOTION_CLASSNAME =
  "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:duration-[var(--modal-open-dur)] data-[state=closed]:duration-[var(--modal-close-dur)] data-[state=open]:ease-[var(--modal-ease)] data-[state=closed]:ease-[var(--modal-ease)] data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 motion-reduce:data-[state=open]:animate-none motion-reduce:data-[state=closed]:animate-none";

export const DIALOG_OVERLAY_CLASSNAME = `fixed inset-0 z-50 bg-overlay-scrim ${MODAL_MOTION_CLASSNAME}`;

// The panel also scales from --modal-scale on open and to --modal-scale-close
// on close, around its center. Centering uses the translate property, which
// the keyframes' transform doesn't touch, so the panel stays centered while it
// animates.
const DIALOG_CONTENT_CLASSNAME = `fixed left-1/2 top-1/2 z-50 grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-2xl border border-border-primary bg-bg-elevated px-4 pb-4 pt-3 shadow-extra-large outline-none origin-center will-change-[transform,opacity] ${MODAL_MOTION_CLASSNAME} data-[state=open]:zoom-in-[var(--modal-scale)] data-[state=closed]:zoom-out-[var(--modal-scale-close)]`;

/**
 * Resolves the panel's classes for a caller `className`, and whether the
 * panel is a size container (see `hasIntrinsicWidth`).
 */
export const getDialogContentClassName = (
  className: string | undefined,
): { className: string; isContainer: boolean } => {
  const merged = cn(
    DIALOG_CONTENT_CLASSNAME,
    !setsOwnWidth(className) && "sm:max-w-md",
    className,
  );
  const isContainer = !hasIntrinsicWidth(merged);
  return { className: cn(isContainer && "@container", merged), isContainer };
};

export const getDialogHeaderClassName = (
  className: string | undefined,
  { isContainer, hasCloseButton }: DialogContentLayout,
): string =>
  cn(
    "flex flex-col gap-1 text-center",
    hasCloseButton && "pr-8",
    isContainer && !hasViewportOverride(className, HEADER_LAYOUT_UTILITIES)
      ? "@sm:text-start"
      : "sm:text-start",
    className,
  );

export const getDialogFooterClassName = (
  className: string | undefined,
  { isContainer }: DialogContentLayout,
): string =>
  cn(
    "flex flex-col-reverse gap-2",
    isContainer && !hasViewportOverride(className, FOOTER_LAYOUT_UTILITIES)
      ? "@sm:flex-row @sm:justify-end"
      : "sm:flex-row sm:justify-end",
    className,
  );

export type DialogTitleSize = "default" | "large";

/**
 * Title Text variant per size, both weight 500: `default` is body-title
 * (16px), `large` is display (20px). There is deliberately no small size.
 */
export const DIALOG_TITLE_VARIANTS = {
  default: "body-title",
  large: "display",
} as const satisfies Record<DialogTitleSize, TextV4Variant>;

/** Added to the title's Text variant. */
export const DIALOG_TITLE_CLASSNAME = "text-text-primary";

/**
 * The close button's `top-*` per title size, so its 24px box is centered on
 * the title's first line: pt-3 (12px) + line-height / 2 - 12px.
 * default: body-title 16px x 1.45 = 23.2px line -> 11.6px, top-3 (12px);
 * large: display 20px x 1.3 = 26px line -> 13px. Dialogs without a
 * DialogTitle use the default offset.
 */
export const DIALOG_CLOSE_BUTTON_TOP_CLASSNAME = {
  default: "top-3",
  large: "top-[13px]",
} as const satisfies Record<DialogTitleSize, string>;

/** Added to the `compact-description` Text variant. */
export const DIALOG_DESCRIPTION_CLASSNAME = "text-pretty text-text-secondary";

/**
 * Builds the layout context a content part provides to its header, footer and
 * description.
 */
export const useDialogContentLayout = (
  isContainer: boolean,
  hasCloseButton: boolean,
): {
  layout: DialogContentLayout;
  descriptionCount: number;
  titleSize: DialogTitleSize;
} => {
  const [descriptionCount, setDescriptionCount] = React.useState(0);
  const registerDescription = React.useCallback(() => {
    setDescriptionCount((count) => count + 1);
    return () => setDescriptionCount((count) => count - 1);
  }, []);
  const [titleSize, setTitleSize] = React.useState<DialogTitleSize>("default");
  const reportTitleSize = React.useCallback((size: DialogTitleSize) => {
    setTitleSize(size);
    return () => setTitleSize("default");
  }, []);
  const layout = React.useMemo(
    () => ({
      isContainer,
      hasCloseButton,
      registerDescription,
      reportTitleSize,
    }),
    [isContainer, hasCloseButton, registerDescription, reportTitleSize],
  );
  return { layout, descriptionCount, titleSize };
};
